import * as THREE from 'three';

export const WEAPON_AIM_MAX_YAW = THREE.MathUtils.degToRad(78);
export const WEAPON_AIM_MAX_PITCH_UP = THREE.MathUtils.degToRad(52);
export const WEAPON_AIM_MAX_PITCH_DOWN = THREE.MathUtils.degToRad(36);
export const WEAPON_AIM_INPUT_CURVE = 1.85;
export const WEAPON_AIM_TURN_SPEED = THREE.MathUtils.degToRad(270);

const localDirection = new THREE.Vector3();
const inverseOrientation = new THREE.Quaternion();

function orientationFor(vehicle, target = new THREE.Quaternion()) {
  if (vehicle?.orientation?.isQuaternion) return target.copy(vehicle.orientation).normalize();
  if (vehicle?.car?.getWorldQuaternion) return vehicle.car.getWorldQuaternion(target).normalize();
  return target.identity();
}

function finiteDirection(value, target) {
  const x = Number(value?.x ?? value?.aimX);
  const y = Number(value?.y ?? value?.aimY);
  const z = Number(value?.z ?? value?.aimZ);
  if (![x, y, z].every(Number.isFinite)) return false;
  target.set(x, y, z);
  return target.lengthSq() > .000001;
}

// Mantiene todo el alcance en el borde del stick, pero comprime fuertemente
// los movimientos pequeños alrededor del centro para permitir correcciones
// precisas. La curva es radial, así que las diagonales no resultan más rápidas.
export function shapeWeaponAimStick(x = 0, y = 0, target = { x:0, y:0 }) {
  const safeX=THREE.MathUtils.clamp(Number(x)||0,-1,1);
  const safeY=THREE.MathUtils.clamp(Number(y)||0,-1,1);
  const rawRadius=Math.hypot(safeX,safeY);
  if(rawRadius<.000001){
    target.x=0;
    target.y=0;
    return target;
  }
  const radius=Math.min(1,rawRadius);
  const shapedRadius=Math.pow(radius,WEAPON_AIM_INPUT_CURVE);
  const scale=shapedRadius/rawRadius;
  target.x=safeX*scale;
  target.y=safeY*scale;
  return target;
}

export function localWeaponAimDirection(yaw = 0, pitch = 0, target = new THREE.Vector3()) {
  const safeYaw = THREE.MathUtils.clamp(Number(yaw) || 0, -WEAPON_AIM_MAX_YAW, WEAPON_AIM_MAX_YAW);
  const safePitch = THREE.MathUtils.clamp(
    Number(pitch) || 0,
    -WEAPON_AIM_MAX_PITCH_DOWN,
    WEAPON_AIM_MAX_PITCH_UP,
  );
  const pitchCos = Math.cos(safePitch);
  return target.set(
    Math.sin(safeYaw) * pitchCos,
    Math.sin(safePitch),
    -Math.cos(safeYaw) * pitchCos,
  ).normalize();
}

export function weaponAimDirection(vehicle, yaw = 0, pitch = 0, target = new THREE.Vector3()) {
  localWeaponAimDirection(yaw, pitch, target);
  return target.applyQuaternion(orientationFor(vehicle)).normalize();
}

export function weaponAimAngles(vehicle, worldDirection, target = { yaw:0, pitch:0 }) {
  if (!finiteDirection(worldDirection, localDirection)) {
    target.yaw = 0;
    target.pitch = 0;
    return target;
  }
  localDirection.normalize().applyQuaternion(
    inverseOrientation.copy(orientationFor(vehicle)).invert(),
  );
  target.yaw = THREE.MathUtils.clamp(
    Math.atan2(localDirection.x, -localDirection.z),
    -WEAPON_AIM_MAX_YAW,
    WEAPON_AIM_MAX_YAW,
  );
  target.pitch = THREE.MathUtils.clamp(
    Math.asin(THREE.MathUtils.clamp(localDirection.y, -1, 1)),
    -WEAPON_AIM_MAX_PITCH_DOWN,
    WEAPON_AIM_MAX_PITCH_UP,
  );
  return target;
}

// Esta misma función se ejecuta en cliente y servidor. El cliente obtiene una
// dirección agradable para el cañón; el servidor vuelve a limitarla al cono
// mecánico del vehículo para que un paquete alterado no pueda disparar atrás.
export function clampWeaponAimDirection(vehicle, value, target = new THREE.Vector3()) {
  const angles = weaponAimAngles(vehicle, value);
  return weaponAimDirection(vehicle, angles.yaw, angles.pitch, target);
}

// El cañón no se teletransporta hacia la mira. Cliente, bots y servidor usan
// esta misma velocidad angular, de modo que mouse y mando comparten la misma
// limitación física y ningún dispositivo obtiene disparos instantáneos.
export function slewWeaponAimDirection(
  vehicle,
  currentValue,
  desiredValue,
  dt=1/60,
  target=new THREE.Vector3(),
  turnSpeed=WEAPON_AIM_TURN_SPEED,
) {
  const current=weaponAimAngles(vehicle,currentValue);
  const desired=weaponAimAngles(vehicle,desiredValue);
  const maximumStep=Math.max(0,Number(dt)||0)*Math.max(0,Number(turnSpeed)||0);
  const yaw=THREE.MathUtils.clamp(
    current.yaw+THREE.MathUtils.clamp(desired.yaw-current.yaw,-maximumStep,maximumStep),
    -WEAPON_AIM_MAX_YAW,
    WEAPON_AIM_MAX_YAW,
  );
  const pitch=THREE.MathUtils.clamp(
    current.pitch+THREE.MathUtils.clamp(desired.pitch-current.pitch,-maximumStep,maximumStep),
    -WEAPON_AIM_MAX_PITCH_DOWN,
    WEAPON_AIM_MAX_PITCH_UP,
  );
  return weaponAimDirection(vehicle,yaw,pitch,target);
}
